import React, { useState } from "react";

export interface CurrentBalanceCardProps {
  asOfLabel?: string;
  balanceAmount?: string;
  balanceSubtitle?: string;
  /** Bar heights for mini chart; last bar is latest books-closed month */
  barHeights?: number[];
  barLabels?: string[];
  barAmounts?: string[];
  momPercent?: string;
  runwayLabel?: string;
  loading?: boolean;
}

/** Books-closed operational cash card (green theme, no runway status pill). */
const cardStyles = {
  text: "text-success-700",
  cardBg: "bg-[#e6f4ea]",
  barFrom: "from-[#bbf7d0]",
  barTo: "to-[#86efac]",
  lastBarBorder: "border-success-600",
  lastBarFrom: "from-[#86efac]",
  lastBarTo: "to-[#22c55e]",
  line: "border-success-400",
  lastDotBorder: "border-success-600",
};

function CurrentBalanceCardSkeleton() {
  return (
    <div
      className="flex flex-col items-start justify-between rounded-lg bg-[#e6f4ea] px-6 py-6 flex-1 min-w-0 animate-pulse"
      aria-hidden
    >
      <div className="flex w-full items-start justify-between">
        <div className="flex flex-col items-start gap-2">
          <div className="h-3.5 w-24 rounded bg-[#4a6b5a]/20" />
          <div className="h-5 w-36 rounded bg-[#4a6b5a]/20" />
        </div>
      </div>
      <div className="flex w-full items-end justify-between gap-4">
        <div className="flex flex-col items-start gap-2">
          <div className="h-10 w-28 rounded bg-[#1a3a2a]/20" />
          <div className="h-4 w-32 rounded bg-[#4a6b5a]/20" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-end gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 w-6 rounded-sm bg-[#86efac]/50" />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-3 w-5 rounded bg-[#4a6b5a]/20" />
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="h-7 w-16 rounded-full bg-[#4a6b5a]/20" />
        <div className="h-7 w-24 rounded-full bg-[#4a6b5a]/20" />
      </div>
    </div>
  );
}

export function CurrentBalanceCard({
  asOfLabel = "As of books closed",
  balanceAmount = "—",
  balanceSubtitle = "Operational cash · Last closed month",
  barHeights = [],
  barLabels = [],
  barAmounts,
  momPercent = "—",
  runwayLabel = "—",
  loading = false,
}: CurrentBalanceCardProps) {
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);

  if (loading) {
    return <CurrentBalanceCardSkeleton />;
  }

  const maxH = barHeights.length > 0 ? Math.max(...barHeights, 1) : 1;

  return (
    <div
      className={`flex flex-col items-start justify-between rounded-lg px-6 py-6 flex-1 min-w-0 ${cardStyles.cardBg}`}
    >
      <div className="flex w-full items-start justify-between">
        <div className="flex flex-col items-start gap-1">
          <span
            className={`font-['Inter'] text-[14px] font-[600] leading-[20px] uppercase tracking-wider ${cardStyles.text}`}
          >
            Past Bank Balance
          </span>
          <span className={`text-caption font-caption ${cardStyles.text}`}>
            {asOfLabel}
          </span>
        </div>
      </div>
      <div className="flex w-full items-end justify-between">
        <div className="flex flex-col items-start gap-1">
          <span className="font-['Inter'] text-[42px] font-[700] leading-[40px] text-[#1a3a2a]">
            {balanceAmount}
          </span>
          <span className="text-caption-bold font-caption-bold text-[#4a6b5a]">
            {balanceSubtitle}
          </span>
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-end gap-2 relative">
            <div className="flex grow shrink-0 basis-0 items-center gap-1 absolute top-[40%] left-0">
              <div
                className={`flex items-start border-t-2 border-dashed flex-1 ${cardStyles.line}`}
              />
            </div>
            {barHeights.map((h, i) => {
              const isLast = i === barHeights.length - 1;
              const label = barLabels[i] ?? "";
              const amount = barAmounts?.[i];
              const isHovered = hoveredBarIndex === i;
              return (
                <div
                  key={i}
                  className="relative flex w-6 flex-none flex-col items-center rounded-sm cursor-default"
                  onMouseEnter={() => setHoveredBarIndex(i)}
                  onMouseLeave={() => setHoveredBarIndex(null)}
                >
                  {isHovered && (amount != null || label) && (
                    <div
                      className="absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md border border-[#d1e9d6] bg-white px-2 py-1.5 shadow-md"
                      role="tooltip"
                    >
                      <span className="text-caption-bold font-caption-bold text-[#1a3a2a]">
                        {label}
                        {amount != null && ` · ${amount}`}
                      </span>
                    </div>
                  )}
                  <div
                    className={`flex w-full flex-col items-center rounded-sm shadow-sm transition-opacity ${
                      isLast
                        ? `pt-4 border-2 border-solid ${cardStyles.lastBarBorder} bg-gradient-to-b ${cardStyles.lastBarFrom} ${cardStyles.lastBarTo} relative`
                        : `bg-gradient-to-b ${cardStyles.barFrom} ${cardStyles.barTo}`
                    } ${isHovered ? "opacity-90" : ""}`}
                    style={{
                      height: Math.max(12, (h / maxH) * 80),
                    }}
                  >
                    {isLast && (
                      <>
                        <div className="pointer-events-none absolute inset-0 rounded-sm bg-gradient-to-t from-transparent to-white opacity-20" />
                        <div
                          className={`relative z-10 flex h-3 w-3 flex-none flex-col items-center rounded-full border-2 border-solid bg-white shadow-sm ${cardStyles.lastDotBorder}`}
                        />
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            {barLabels.map((l, i) => (
              <span
                key={i}
                className={`text-caption font-caption text-[#4a6b5a] ${i === barLabels.length - 1 ? `text-caption-bold font-caption-bold ${cardStyles.text}` : ""}`}
              >
                {l}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center rounded-full border border-solid border-[#d1e9d6] bg-white px-3 py-1">
          <span className={`text-body-bold font-body-bold ${cardStyles.text}`}>
            {momPercent}
          </span>
        </div>
        <div className="flex items-center rounded-full border border-solid border-[#d1e9d6] bg-white px-3 py-1">
          <span className="text-body-bold font-body-bold text-neutral-700">
            {runwayLabel}
          </span>
        </div>
      </div>
    </div>
  );
}
